import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { JWT_KEY } from "../../config/constant";
import { renewAccessToken } from "../auth";

export interface BaseResponse<T = any> {
  code: number;
  isSuccess: boolean;
  message: string;
  result: T;
}

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<BaseResponse>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const request: CustomInstance = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    accept: "application/json",
  },
});

request.interceptors.request.use(
  (config) => {
    const jwt = window.localStorage.getItem(JWT_KEY) ?? "";

    if (!jwt) return config;

    if (config.headers && !config.headers.Authorization)
      config.headers.Authorization = `Bearer ${jwt}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// ──────────────────────────────────────────────────────────────
// 2026-04-19 fix(#3): 토큰 재발급 하드닝
// (기존) 외주사가 2024-05 PR #451로 renewPromise 단일화는 했지만,
//   1) renewAccessToken()이 throw하면 모든 awaiter에 전파 → 다중 logout
//   2) 재발급 성공 후에도 401 반복되면 무한 루프 발생 가능
// (수정)
//   1) .catch(() => null)로 throw를 정규화
//   2) 재시도 플래그로 한 요청당 최대 1회만 재발급 → 인증 확정 실패 처리
// ──────────────────────────────────────────────────────────────
let renewPromise: Promise<string | null> | null = null;
const clearRenewPromise = () => (renewPromise = null);

// 재발급 후에도 동일 요청이 또 401이면 Loop로 간주하고 확정 실패 처리
interface InternalRetryConfig extends AxiosRequestConfig {
  _bcRetriedAfterRenew?: boolean;
}

request.interceptors.response.use(
  async ({ config, data }) => {
    if (data.code === 401) {
      // 재발급 후 재시도 경험이 있으면 무한 루프 차단
      if ((config as InternalRetryConfig)._bcRetriedAfterRenew) {
        throw new Error("토큰 재발급 실패");
      }

      // renew에 대한 Promise 객체가 없을 경우
      if (!renewPromise) {
        // 신규 토큰요청을 보내고, 끝나면 clear
        // .catch로 throw를 null 리턴으로 정규화 → awaiter 다수에 예외 전파 방지
        renewPromise = renewAccessToken()
          .catch((e) => {
            console.log("renewAccessToken error:", e);
            return null;
          })
          .finally(clearRenewPromise);
      }

      // Promise 끝날 때까지 대기
      const accessToken = await renewPromise;

      // 토큰이 정상적으로 넘어왔고, 이전 요청이 토큰이 있었다면
      if (accessToken && config.headers?.Authorization) {
        // 헤더 토큰 새로 넣기
        config.headers.Authorization = `Bearer ${accessToken}`;

        // 재시도 플래그 설정 후 다시 요청 보내기
        (config as InternalRetryConfig)._bcRetriedAfterRenew = true;
        return request(config);
      } else {
        throw new Error("토큰 재발급 실패");
      }
    } else if (
      data.message === "이미 탈퇴한 회원 입니다." ||
      data.message.includes("로그인 절차를 완료 하지 않은 유저 입니다.")
    ) {
      console.log(data.message);
      localStorage.clear();
      window.location.href = `${process.env.REACT_APP_URL}/login`;
      return data;
    }

    return data;
  },
  (error) => Promise.reject(error)
);

export default request;
